/**
 * CreateEntryModal — the create-entry form (plan §5.2: official_name
 * required, section/code_type/code optional; §13.4 WP-33b frontend scope
 * "a create-entry form").
 */
import { useState, FormEvent } from 'react';
import { Modal, fieldCls, FilterSelect, primaryBtnCls, secondaryBtnCls, useToast } from '../../../../ui';
import { createMasterEntry } from '../../../api/client';
import type { MasterEntryRow, MasterListType, MasterListSection } from '../../../api/client';
import { MASTER_LIST_TYPE_LABEL, MASTER_SECTION_OPTIONS } from './listTypes';

interface Props {
  isOpen: boolean;
  listType: MasterListType;
  onClose: () => void;
  onCreated: (entry: MasterEntryRow) => void;
}

export function CreateEntryModal({ isOpen, listType, onClose, onCreated }: Props) {
  const { toast } = useToast();
  const [officialName, setOfficialName] = useState('');
  const [section, setSection] = useState<'' | MasterListSection>('');
  const [codeType, setCodeType] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setOfficialName('');
    setSection('');
    setCodeType('');
    setCode('');
    setError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!officialName.trim()) {
      setError('A name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const entry = await createMasterEntry(listType, {
        official_name: officialName.trim(),
        section: section || null,
        code_type: codeType.trim() || null,
        code: code.trim() || null,
      });
      toast(`${entry.official_name} added.`, { type: 'success' });
      onCreated(entry);
      handleClose();
    } catch (err) {
      const res = (err as { response?: { status?: number; data?: any } })?.response;
      if (res?.status === 409 && typeof res.data?.error === 'string') {
        setError(res.data.error);
      } else if (typeof res?.data?.error === 'string') {
        setError(res.data.error);
      } else {
        setError('Something went wrong creating the entry.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`New ${MASTER_LIST_TYPE_LABEL[listType].toLowerCase()} entry`}>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-entry-form">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="cta-master-name" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Name
          </label>
          <input
            id="cta-master-name"
            type="text"
            value={officialName}
            onChange={(e) => setOfficialName(e.target.value)}
            className={`${fieldCls} w-full`}
            autoFocus
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Section</span>
            <FilterSelect
              label="Section"
              value={section}
              onChange={(v) => setSection(v as '' | MasterListSection)}
              options={MASTER_SECTION_OPTIONS}
            />
          </div>
          <div>
            <label htmlFor="cta-master-code-type" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Code type
            </label>
            <input
              id="cta-master-code-type"
              type="text"
              placeholder="CPT"
              value={codeType}
              onChange={(e) => setCodeType(e.target.value)}
              className={`${fieldCls} w-full`}
            />
          </div>
          <div>
            <label htmlFor="cta-master-code" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Code
            </label>
            <input
              id="cta-master-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`${fieldCls} w-full`}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={handleClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} className={primaryBtnCls}>
            {submitting ? 'Creating…' : 'Create entry'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateEntryModal;
